import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createClient } from '@blinkdotnew/sdk';
import { requestThrottler } from '@/lib/requestThrottler';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Mail,
  MessageSquare,
  Workflow,
  Home,
  Settings,
  LogOut,
  PenSquare,
  Menu,
  X,
  Sparkles,
  FlaskConical,
  Calendar,
  User,
  Users,
  Activity,
  Clock,
  Crown,
  History
} from 'lucide-react';
import { ResizableAIChat } from '@/components/ResizableAIChat';

interface User {
  id: string;
  email: string;
  name?: string;
  avatar?: string;
  isTestMode?: boolean;
}

interface AppLayoutProps {
  children: ReactNode;
  user: User;
  onLogout: () => void;
}

export default function AppLayout({ children, user, onLogout }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [aiPanelWidth, setAiPanelWidth] = useState(400);
  const [aiPanelCollapsed, setAiPanelCollapsed] = useState(false);
  const [lastSyncInfo, setLastSyncInfo] = useState<{ time: string; account: string } | null>(null);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Email Inbox', href: '/inbox', icon: Mail },
    { name: 'Calendar', href: '/calendar', icon: Calendar },
    { name: 'Timeline', href: '/timeline', icon: History },
    { name: 'Contact Intelligence', href: '/contacts', icon: Users },
    { name: 'Top 50', href: '/top-50', icon: Crown },
    { name: 'AI Chat', href: '/ai-chat', icon: MessageSquare },
    { name: 'Workflows', href: '/workflows', icon: Workflow },
    { name: 'Compose', href: '/compose', icon: PenSquare },
    { name: 'Settings', href: '/settings', icon: Settings },
    { name: 'UI Monitor', href: '/ui-monitoring', icon: Activity },
    { name: 'Live Email Test', href: '/live-email-test', icon: FlaskConical },
  ];

  // Load last sync info on mount with throttling
  useEffect(() => {
    const loadSyncInfo = async () => {
      if (!user) return;
      
      try {
        const accounts = await requestThrottler.enqueue(
          `sync-info-${user.id}`,
          () => createClient({ 
            projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
            authRequired: false 
          }).db.emailAccounts.list({
            where: { userId: user.id },
            orderBy: { lastSyncAt: 'desc' },
            limit: 1
          }),
          { 
            cacheTime: 30000, // Cache for 30 seconds
            priority: 3 // Lower priority
          }
        );
        
        if (accounts.length > 0 && accounts[0].lastSyncAt) {
          setLastSyncInfo({
            time: accounts[0].lastSyncAt,
            account: accounts[0].emailAddress || accounts[0].provider
          });
        }
      } catch (error: any) {
        // Only log non-rate-limit errors
        if (!error?.message?.includes('rate limit')) {
          console.error('Failed to load sync info:', error);
        }
      }
    };
    
    loadSyncInfo();
    
    // Reload every 60 seconds (reduced from 30s to minimize API calls)
    const interval = setInterval(loadSyncInfo, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed inset-y-0 left-0 z-50 bg-card/95 backdrop-blur-xl border-r border-border/50 shadow-lg transition-all duration-300 ease-in-out lg:translate-x-0 ${
          isHovered ? 'lg:w-64' : 'lg:w-16'
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-shadow duration-200">
                <Sparkles className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className={`text-lg font-semibold transition-opacity duration-300 whitespace-nowrap ${isHovered ? 'opacity-100' : 'lg:opacity-0 lg:hidden'}`}>
                Naible-Thread
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-md hover:shadow-lg'
                      : 'text-muted-foreground hover:bg-secondary/80 hover:text-foreground hover:shadow-sm'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={`h-5 w-5 flex-shrink-0 ${isHovered ? 'mr-3' : 'lg:mr-0'}`} />
                  <span className={`transition-opacity duration-300 whitespace-nowrap ${isHovered ? 'opacity-100' : 'lg:opacity-0 lg:hidden'}`}>
                    {item.name}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="border-t border-border/50 p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start px-2 hover:bg-secondary/80 transition-all duration-200">
                  <Avatar className="h-8 w-8 flex-shrink-0 ring-2 ring-border/50 hover:ring-primary/50 transition-all duration-200">
                    <AvatarImage src={user.avatar} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {user.email?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className={`flex-1 text-left ml-3 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'lg:opacity-0 lg:hidden'}`}>
                    <p className="text-sm font-medium truncate">{user.name || 'Demo User'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel>
                  My Account
                  {user.isTestMode && (
                    <span className="ml-2 text-xs text-primary font-normal flex items-center">
                      <FlaskConical className="w-3 h-3 mr-1" />
                      Test Mode
                    </span>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  View Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div 
        className={`transition-all duration-300 ${isHovered ? 'lg:pl-64' : 'lg:pl-16'}`}
        style={{ 
          paddingRight: aiPanelCollapsed ? '48px' : `${aiPanelWidth}px` 
        }}
      >
        {/* Mobile header */}
        <div className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border/50 bg-card/95 backdrop-blur-xl shadow-sm px-4 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center shadow-md">
            <Sparkles className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-semibold flex-1">Naible-Thread</span>
          {lastSyncInfo && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {(() => {
                const syncDate = new Date(lastSyncInfo.time);
                const diff = Date.now() - syncDate.getTime();
                const minutes = Math.floor(diff / 60000);
                return minutes < 1 ? 'now' : minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h`;
              })()}
            </div>
          )}
        </div>

        {/* Page content */}
        <main className="min-h-[calc(100vh-4rem)] lg:min-h-screen">
          <div className="gradient-mesh opacity-30 fixed inset-0 pointer-events-none" />
          <div className="relative z-10 p-6 sm:p-8">{children}</div>
        </main>
      </div>

      {/* AI Chat Panel - Always available on right side */}
      <ResizableAIChat 
        onWidthChange={setAiPanelWidth}
        onCollapseChange={setAiPanelCollapsed}
      />
    </div>
  );
}